export const authorCountKey = (author) => `${author?.given?.split(' ')?.[0] || ''} ${author?.family || ''}`.trim()

/**
 * Applies filters to citation returning true/false if it passes/fails
 * */
export const filterCitation = (
  citation,
  papers,
  {
    supporting,
    mentioning,
    contradicting,
    unclassified,

    publishedRange,
    textMatch,

    citationIDs,
    sourceDOIIDs,

    selfCite,
    independent,

    authors,
    journals,
    pubtypes,
    sections
  },
  doiKey = 'source',
  sectionMap = null
) => {
  const paper = papers[citation[doiKey]]
  const year = paper?.year
  const section = (citation.section || '').toLowerCase().trim()
  let textToMatch = ''
  textToMatch += citation.snippet || citation.snippets?.reduce((acc, text) => acc + text, '') || ''
  textToMatch += section
  textToMatch += paper?.title || ''
  for (const author of (paper?.authors || [])) {
    textToMatch += author.given || ''
    textToMatch += ' '
    textToMatch += author.family || ''
  }
  textToMatch += paper?.year || ''
  textToMatch += paper?.journal || ''
  textToMatch += paper?.shortJournal || ''
  textToMatch += paper?.publisher || ''
  textToMatch += paper?.abstract || ''
  textToMatch = textToMatch.toLowerCase()

  const inRange = !year || (year >= publishedRange?.min && year <= publishedRange?.max)

  const filterTypes = (
    typeof supporting === 'boolean' ||
    typeof contradicting === 'boolean' ||
    typeof mentioning === 'boolean' ||
    typeof unclassified === 'boolean'
  ) && !(
    !supporting && !contradicting && !mentioning && !unclassified
  ) // all false means no filter applied

  const filterAuthorshipTypes = (
    typeof selfCite === 'boolean' ||
    typeof independent === 'boolean'
  ) && !(
    !selfCite && !independent
  ) // all false means no filter applied

  const validTypes = []
  if (supporting) validTypes.push('supporting')
  if (mentioning) validTypes.push('mentioning')
  if (contradicting) validTypes.push('contradicting')
  if (unclassified) validTypes.push('unclassified')

  if (publishedRange && !inRange) {
    return false
  }
  if (journals?.length > 0 && !journals?.includes(paper?.journal)) {
    return false
  }
  if (pubtypes?.length > 0 && paper?.normalizedTypes?.filter(type => pubtypes?.includes(type)).length === 0) {
    return false
  }

  if (sectionMap && sections?.length > 0 && !sections.includes(sectionMap[section])) {
    return false
  }

  if (!sectionMap && sections?.length > 0 && !sections.includes(section)) {
    return false
  }

  if (authors?.length > 0 && paper?.authors?.filter(
    author => authors?.includes(authorCountKey(author))
  )?.length === 0) {
    return false
  }

  if (citation.type && filterTypes && !validTypes.includes(citation.type)) {
    return false
  }

  if (filterTypes && (!supporting || !contradicting || !mentioning) && !citation.snippet) {
    return false
  }

  if (filterAuthorshipTypes && !independent && (!citation?.selfCites || citation?.selfCites?.length === 0)) {
    return false
  }

  if (filterAuthorshipTypes && !selfCite && citation?.selfCites?.length > 0) {
    return false
  }

  if (filterAuthorshipTypes && !selfCite && !independent) {
    return false
  }

  if (textMatch && !textToMatch.includes(textMatch.toLowerCase())) {
    return false
  }

  if (sourceDOIIDs?.length > 0 && !sourceDOIIDs.includes(citation.source)) {
    return false
  }

  if (citationIDs?.length > 0 && !citationIDs.includes(`${citation.id}`)) {
    return false
  }

  return true
}

export const getSectionCounts = (citations, sectionMap) => {
  const result = {}
  for (const citation of citations) {
    if (!citation.section) {
      continue
    }

    let section = citation.section?.toLowerCase().trim() || ''
    if (sectionMap) {
      section = sectionMap[section]
    }

    if (!section) {
      if (!result.other) {
        result.other = 0
      }
      result.other += 1
      continue
    }

    if (!result[section]) {
      result[section] = 0
    }
    result[section] += 1
  }
  return Object.entries(result).sort(([, a], [, b]) => b - a) || []
}

export const getAuthorRelationshipCounts = (citations, papers, doiKey = 'source') => ({
  selfCite: citations.filter(citation => filterCitation(citation, papers, { selfCite: true }, doiKey)).length,
  independent: citations.filter(citation => filterCitation(citation, papers, { independent: true }, doiKey)).length
})

/**
 * Applies filters to papers with citations returning true/false if it passes/fails
 * */
export const filterPapersWithCitations = (
  papersWithCitations,
  {

    authors,
    journals,
    pubtypes,

    publishedRange,
    textMatch,

    selfCite,
    independent
  }
) => {
  const year = papersWithCitations?.paper?.year
  let textToMatch = ''
  textToMatch += papersWithCitations?.paper?.title || ''
  for (const author of (papersWithCitations?.paper?.authors || [])) {
    textToMatch += author.given || ''
    textToMatch += ' '
    textToMatch += author.family || ''
  }
  textToMatch += papersWithCitations?.paper?.year || ''
  textToMatch += papersWithCitations?.paper?.journal || ''
  textToMatch += papersWithCitations?.paper?.shortJournal || ''
  textToMatch += papersWithCitations?.paper?.publisher || ''
  textToMatch += papersWithCitations?.paper?.abstract || ''
  textToMatch += papersWithCitations?.citations?.map(cite => cite.snippet || '')?.join(' ') || ''
  textToMatch = textToMatch.toLowerCase()

  const inRange = !year || (year >= publishedRange?.min && year <= publishedRange?.max)

  const filterAuthorshipTypes = (
    typeof selfCite === 'boolean' ||
    typeof independent === 'boolean'
  ) && !(
    !selfCite && !independent
  ) // all false means no filter applied

  if (publishedRange && !inRange) {
    return false
  }

  const sampleCitation = papersWithCitations?.citations?.length > 0 && papersWithCitations?.citations?.[0]
  if (filterAuthorshipTypes && !independent && selfCite && (!sampleCitation?.selfCites || sampleCitation?.selfCites?.length === 0)) {
    return false
  }

  if (filterAuthorshipTypes && !selfCite && independent && sampleCitation?.selfCites?.length > 0) {
    return false
  }

  if (filterAuthorshipTypes && !selfCite && !independent) {
    return false
  }

  if (textMatch && !textToMatch.includes(textMatch.toLowerCase())) {
    return false
  }

  if (journals?.length > 0 && !journals?.includes(papersWithCitations?.paper?.journal)) {
    return false
  }
  if (pubtypes?.length > 0 && papersWithCitations?.paper?.normalizedTypes?.filter(type => pubtypes?.includes(type)).length === 0) {
    return false
  }
  if (authors?.length > 0 && papersWithCitations?.paper?.authors?.filter(
    author => authors?.includes(authorCountKey(author))
  )?.length === 0) {
    return false
  }

  return true
}
